/*
 |-----------------------------------------------------------------------------
 | Matemática de color — TypeScript puro, sin React ni DOM
 |-----------------------------------------------------------------------------
 | Igual que `upload/`, esto es núcleo: se puede probar en un nodo suelto y
 | sobreviviría a un cambio de framework sin tocar una línea.
 |
 | Por qué OKLCH y no hex o HSL. El estudio nos da UN color y hay que derivar
 | de ahí una interfaz entera: hover, activo, deshabilitado, fondos teñidos y
 | una rampa de grises que no traicione su marca. En HSL eso no funciona —
 | subir la L de un amarillo y de un azul el mismo 10 % produce dos saltos de
 | brillo percibido completamente distintos, y el resultado es que unos temas
 | quedan bien y otros ilegibles. La L de OKLCH SÍ es luminosidad percibida:
 | mover 0,05 se ve igual en cualquier matiz.
 |
 | Y lo que de verdad importa: permite CALCULAR el contraste en vez de
 | esperarlo. Hoy los botones son `text-white` siempre — un estudio con una
 | marca amarilla tiene botones ilegibles y nada lo impide.
 */

export interface Oklch {
  /** Luminosidad percibida, 0 (negro) a 1 (blanco). */
  l: number
  /** Croma. 0 es gris; por encima de ~0,37 no hay sRGB que lo represente. */
  c: number
  /** Matiz en grados, 0–360. */
  h: number
}

/** Canales sRGB de 0 a 255, ya redondeados. */
export type Rgb = readonly [number, number, number]

const clamp = (value: number, min: number, max: number): number =>
  value < min ? min : value > max ? max : value

/* -------------------------------------------------------------------------- */
/* Conversión                                                                  */
/* -------------------------------------------------------------------------- */

function srgbToLinear(channel: number): number {
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
}

function linearToSrgb(channel: number): number {
  return channel <= 0.0031308 ? channel * 12.92 : 1.055 * channel ** (1 / 2.4) - 0.055
}

/**
 * Acepta `#rgb` y `#rrggbb`, con o sin almohadilla.
 *
 * Devuelve null en vez de lanzar: el color llega de la base de datos y de un
 * formulario del estudio, así que un valor corrupto tiene que degradar al tema
 * por defecto, no tumbar la galería que el cliente vino a ver.
 */
export function parseHex(input: string): Rgb | null {
  const hex = input.trim().replace(/^#/, '')

  if (hex.length === 3) {
    const [r, g, b] = [...hex].map((digit) => parseInt(digit + digit, 16))

    return r !== undefined && g !== undefined && b !== undefined && !Number.isNaN(r * g * b)
      ? [r, g, b]
      : null
  }

  if (hex.length !== 6 || !/^[0-9a-f]{6}$/i.test(hex)) return null

  return [
    parseInt(hex.slice(0, 2), 16),
    parseInt(hex.slice(2, 4), 16),
    parseInt(hex.slice(4, 6), 16),
  ]
}

/** sRGB (0–255) → OKLCH. Algoritmo de Björn Ottosson. */
export function rgbToOklch([r255, g255, b255]: Rgb): Oklch {
  const r = srgbToLinear(r255 / 255)
  const g = srgbToLinear(g255 / 255)
  const b = srgbToLinear(b255 / 255)

  const lCone = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const mCone = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const sCone = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)

  const l = 0.2104542553 * lCone + 0.793617785 * mCone - 0.0040720468 * sCone
  const a = 1.9779984951 * lCone - 2.428592205 * mCone + 0.4505937099 * sCone
  const bAxis = 0.0259040371 * lCone + 0.7827717662 * mCone - 0.808675766 * sCone

  const chroma = Math.sqrt(a * a + bAxis * bAxis)
  const hue = chroma < 1e-6 ? 0 : ((Math.atan2(bAxis, a) * 180) / Math.PI + 360) % 360

  return { l, c: chroma, h: hue }
}

/** OKLCH → sRGB lineal, SIN comprobar que quepa en la gama. Uso interno. */
function oklchToLinearRgb({ l, c, h }: Oklch): [number, number, number] {
  const radians = (h * Math.PI) / 180
  const a = c * Math.cos(radians)
  const b = c * Math.sin(radians)

  const lCone = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3
  const mCone = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3
  const sCone = (l - 0.0894841775 * a - 1.291485548 * b) ** 3

  return [
    4.0767416621 * lCone - 3.3077115913 * mCone + 0.2309699292 * sCone,
    -1.2684380046 * lCone + 2.6097574011 * mCone - 0.3413193965 * sCone,
    -0.0041960863 * lCone - 0.7034186147 * mCone + 1.707614701 * sCone,
  ]
}

const EPSILON = 1e-4

const fitsInSrgb = (linear: [number, number, number]): boolean =>
  linear.every((channel) => channel >= -EPSILON && channel <= 1 + EPSILON)

/**
 * OKLCH → sRGB (0–255).
 *
 * OKLCH describe más colores de los que una pantalla sRGB puede mostrar, así
 * que un color derivado puede caerse fuera de la gama. Recortar los canales sin
 * más cambia el MATIZ —el azul se vuelve violeta— que es justo lo que no
 * podemos hacer con la marca de un cliente. En vez de eso se baja el croma por
 * bisección hasta que entra, conservando luminosidad y matiz intactos.
 */
export function oklchToRgb(color: Oklch): Rgb {
  const direct = oklchToLinearRgb(color)

  if (fitsInSrgb(direct)) return toBytes(direct)

  let low = 0
  let high = color.c
  let best = oklchToLinearRgb({ ...color, c: 0 })

  // 18 pasos dejan el error de croma por debajo de lo que un ojo distingue.
  for (let step = 0; step < 18; step++) {
    const mid = (low + high) / 2
    const candidate = oklchToLinearRgb({ ...color, c: mid })

    if (fitsInSrgb(candidate)) {
      best = candidate
      low = mid
    } else {
      high = mid
    }
  }

  return toBytes(best)
}

function toBytes(linear: [number, number, number]): Rgb {
  return linear.map((channel) => Math.round(clamp(linearToSrgb(channel), 0, 1) * 255)) as unknown as Rgb
}

/* -------------------------------------------------------------------------- */
/* Contraste                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Luminancia relativa de la WCAG 2.
 *
 * Se usa esta y no la L de OKLCH aunque tengamos OKLCH a mano: la L perceptual
 * sirve para CONSTRUIR la rampa, pero el umbral que hay que cumplir para que la
 * interfaz sea accesible está definido sobre esta fórmula. Aproximarlo con otra
 * métrica es decir que cumples sin haberlo comprobado.
 */
function relativeLuminance([r, g, b]: Rgb): number {
  const channel = (byte: number): number => {
    const value = byte / 255

    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  }

  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/** Ratio de contraste WCAG entre dos colores. De 1 (idénticos) a 21. */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const first = relativeLuminance(a)
  const second = relativeLuminance(b)
  const lighter = Math.max(first, second)
  const darker = Math.min(first, second)

  return (lighter + 0.05) / (darker + 0.05)
}

/** Texto normal sobre un fondo: AA. */
export const CONTRAST_TEXT = 4.5

/** Componente de interfaz (el botón contra la página): AA para no-texto. */
export const CONTRAST_UI = 3

/**
 * Elige entre dos tintas la que más contrasta con el fondo.
 *
 * Sustituye al `text-white` fijo que hay hoy en los catorce botones primarios.
 */
export function pickForeground(background: Rgb, options: readonly Rgb[]): Rgb {
  return options.reduce((best, candidate) =>
    contrastRatio(candidate, background) > contrastRatio(best, background) ? candidate : best,
  )
}

/* -------------------------------------------------------------------------- */
/* Ajuste del color de marca                                                   */
/* -------------------------------------------------------------------------- */

export interface AccentFit {
  accent: Rgb
  foreground: Rgb
  /** Contraste conseguido de la etiqueta sobre el acento. */
  textRatio: number
  /** El PEOR contraste del acento contra los fondos sobre los que se pinta. */
  surfaceRatio: number
  /** false si el color del estudio no admite ninguna L que cumpla ambos. */
  accessible: boolean
}

/**
 * Busca la luminosidad a la que el color del estudio funciona de verdad.
 *
 * Hay dos condiciones simultáneas y tiran en direcciones opuestas:
 *
 *   1. La etiqueta tiene que leerse SOBRE el botón (4,5:1).
 *   2. El botón tiene que distinguirse de la página (3:1).
 *
 * Un azul marino cumple (1) con texto blanco y falla (2) en un tema oscuro:
 * el botón se funde con el fondo. Un amarillo cumple (2) en oscuro y falla (1)
 * con blanco encima. Por eso no vale una regla de umbral —«si L > 0,6, texto
 * oscuro»— sino recorrer el rango y quedarse con la L más cercana a la marca
 * original entre las que cumplen las dos. Se conserva matiz y croma siempre:
 * el estudio sigue viendo SU color, sólo que a un brillo que funciona.
 *
 * Si ninguna L cumple —una marca casi blanca sobre un tema casi blanco—
 * devuelve la mejor disponible con `accessible: false`, para que los ajustes
 * puedan avisar en lugar de publicar en silencio una galería ilegible.
 *
 * `backgrounds` es una LISTA porque el acento no se pinta sólo sobre la página:
 * también rellena la pista de los medidores, que es más oscura. Ajustar contra
 * la página y dar por hecho el resto dejaba barras de cuota por debajo de 3:1
 * con las marcas claras —amarillo, lima, naranja—, que son justo las que
 * quedan al filo del umbral contra el blanco. Se exige el peor de todos.
 */
export function fitAccent(
  brand: Oklch,
  backgrounds: readonly Rgb[],
  inks: readonly Rgb[],
  range: readonly [number, number],
): AccentFit {
  const [minL, maxL] = range
  let best: AccentFit | null = null
  let bestScore = -Infinity

  // Paso de 0,005 sobre un rango típico de 0,3 son ~60 candidatos: instantáneo,
  // y más fino que cualquier diferencia perceptible.
  for (let l = minL; l <= maxL + 1e-9; l += 0.005) {
    const accent = oklchToRgb({ ...brand, l })
    const foreground = pickForeground(accent, inks)
    const textRatio = contrastRatio(foreground, accent)
    const surfaceRatio = Math.min(...backgrounds.map((bg) => contrastRatio(accent, bg)))

    const meetsBoth = textRatio >= CONTRAST_TEXT && surfaceRatio >= CONTRAST_UI

    // Entre los que cumplen se prefiere el más fiel a la marca; entre los que
    // no, el que menos lejos se queda. Sumar 1000 mantiene ambos universos
    // separados sin necesidad de dos bucles.
    const score = meetsBoth
      ? 1000 - Math.abs(l - brand.l)
      : Math.min(textRatio / CONTRAST_TEXT, 1) + Math.min(surfaceRatio / CONTRAST_UI, 1)

    if (score > bestScore) {
      bestScore = score
      best = { accent, foreground, textRatio, surfaceRatio, accessible: meetsBoth }
    }
  }

  // El bucle siempre entra al menos una vez con un rango válido, pero el tipo
  // no lo sabe y un rango invertido por configuración no debe reventar.
  return (
    best ?? {
      accent: oklchToRgb(brand),
      foreground: inks[0] ?? [255, 255, 255],
      textRatio: 1,
      surfaceRatio: 1,
      accessible: false,
    }
  )
}

/** Formato que espera Tailwind para poder aplicar alfa: `"37 99 235"`. */
export function toChannels([r, g, b]: Rgb): string {
  return `${r} ${g} ${b}`
}
